import { v4 as uuid } from "uuid"
import type { TFunction } from "i18next"
import i18n from "@/lib/i18n/i18n"
import type { Route2g, RouteStop } from "@/reducers/routes2gSlice"
import type { RouteInfo } from "@/components/maps/RouteView"
import type { AvoidType, CarRouteConfig, LoadType } from "@/api/adHocCommands"
import type { VehicleType } from "@/components/analyzeOrderOnPage/VehicleTypeSelect"
import type { SelectedDriverLocation } from "@/components/analyzeOrderOnPage/DriverSelect"
import type {
  AlternativeRoute,
  PerLegAlternatives,
} from "@/lib/planRouteParser"
import type { SavedRouteState } from "@/lib/routeStateUtils"

// Tabs in "Plan route on map": a dispatcher keeps several routes (often one
// per driver) open side by side and names them, instead of clearing one route
// to check another and digging the first one out of the history afterwards.
export const PLAN_ROUTE_TABS_FLAG = "plan-route-tabs"

// Every tab keeps its whole route with alternatives in localStorage, which is
// shared with the saved-routes history and has a hard quota.
export const MAX_PLAN_ROUTE_TABS = 10

const STORAGE_KEY = "plan_route_tabs"

// What a tab remembers of the planner: everything the planner restores from
// a saved route, plus the selected driver.
export interface PlanRouteSnapshot {
  temporaryRoute?: Route2g
  temporaryRouteInfo?: RouteInfo
  selectedAvoidOptions: AvoidType[]
  selectedLoadType: LoadType[]
  selectedVehicleType: VehicleType
  selectedDriver?: SelectedDriverLocation
  driverCarConfig?: CarRouteConfig
  // Average fuel consumption (l/100km) entered on top of the vehicle preset
  fuelConsumption?: string
  messageText?: string
  alternativeRoutes?: AlternativeRoute[]
  perLegAlternatives?: PerLegAlternatives[]
}

export interface PlanRouteTab {
  id: string
  // Shown as "Route <number>" until the route has stops to name it by.
  number: number
  // Set only when the dispatcher names the tab. Until then the label
  // follows the route.
  name?: string
  snapshot?: PlanRouteSnapshot
}

export interface PlanRouteTabsState {
  tabs: PlanRouteTab[]
  activeTabId: string
}

const lowestFreeNumber = (tabs: PlanRouteTab[]): number => {
  const used = new Set(tabs.map((tab) => tab.number))
  let number = 1
  while (used.has(number)) number++
  return number
}

const newTab = (
  tabs: PlanRouteTab[],
  snapshot?: PlanRouteSnapshot,
): PlanRouteTab => ({
  id: uuid(),
  number: lowestFreeNumber(tabs),
  snapshot,
})

export const initialTabsState = (): PlanRouteTabsState => {
  const tab = newTab([])
  return { tabs: [tab], activeTabId: tab.id }
}

export const findActiveTab = (state: PlanRouteTabsState): PlanRouteTab =>
  state.tabs.find((tab) => tab.id === state.activeTabId) ?? state.tabs[0]

export const isSnapshotEmpty = (snapshot?: PlanRouteSnapshot): boolean =>
  !snapshot?.temporaryRoute?.stops?.length

// A tab worth an undo when closed: it has a route or a name.
export const hasTabContent = (tab: PlanRouteTab): boolean =>
  Boolean(tab.name) || !isSnapshotEmpty(tab.snapshot)

export const canAddTab = (state: PlanRouteTabsState): boolean =>
  state.tabs.length < MAX_PLAN_ROUTE_TABS

export const addTab = (
  state: PlanRouteTabsState,
  snapshot?: PlanRouteSnapshot,
): PlanRouteTabsState => {
  if (!canAddTab(state)) return state
  const tab = newTab(state.tabs, snapshot)
  return { tabs: [...state.tabs, tab], activeTabId: tab.id }
}

export const selectTab = (
  state: PlanRouteTabsState,
  tabId: string,
): PlanRouteTabsState =>
  state.tabs.some((tab) => tab.id === tabId)
    ? { ...state, activeTabId: tabId }
    : state

// Closing the active tab moves to its right-hand neighbour (or the left one
// when it was the last). Closing the only tab leaves a fresh empty one, so
// the planner always has a tab to work in.
export const closeTab = (
  state: PlanRouteTabsState,
  tabId: string,
): PlanRouteTabsState => {
  const index = state.tabs.findIndex((tab) => tab.id === tabId)
  if (index === -1) return state
  const tabs = state.tabs.filter((tab) => tab.id !== tabId)
  if (tabs.length === 0) return initialTabsState()
  if (state.activeTabId !== tabId) return { ...state, tabs }
  const neighbour = tabs[Math.min(index, tabs.length - 1)]
  return { tabs, activeTabId: neighbour.id }
}

// Puts a closed tab back where it was and makes it active again.
export const restoreTab = (
  state: PlanRouteTabsState,
  tab: PlanRouteTab,
  index: number,
): PlanRouteTabsState => {
  if (state.tabs.some((existing) => existing.id === tab.id)) return state
  // The fresh tab left behind by closing the only tab gives way again.
  const onlyFreshTab = state.tabs.length === 1 && !hasTabContent(state.tabs[0])
  const tabs = onlyFreshTab ? [] : state.tabs
  if (tabs.length >= MAX_PLAN_ROUTE_TABS) return state
  const number = tabs.some((existing) => existing.number === tab.number)
    ? lowestFreeNumber(tabs)
    : tab.number
  const restored = [...tabs]
  restored.splice(Math.min(index, restored.length), 0, { ...tab, number })
  return { tabs: restored, activeTabId: tab.id }
}

export const renameTab = (
  state: PlanRouteTabsState,
  tabId: string,
  name: string,
): PlanRouteTabsState => {
  const trimmed = name.trim()
  return {
    ...state,
    tabs: state.tabs.map((tab) =>
      tab.id === tabId ? { ...tab, name: trimmed || undefined } : tab,
    ),
  }
}

export const updateTabSnapshot = (
  state: PlanRouteTabsState,
  tabId: string,
  snapshot: PlanRouteSnapshot,
): PlanRouteTabsState => ({
  ...state,
  tabs: state.tabs.map((tab) =>
    tab.id === tabId ? { ...tab, snapshot } : tab,
  ),
})

// A route picked from the history opens in the active tab when that tab is
// still empty, and in a new tab otherwise, so it never replaces work in
// progress. With every tab taken it replaces the active tab's route; the
// replaced route is already in the history.
export const openRouteInTab = (
  state: PlanRouteTabsState,
  snapshot: PlanRouteSnapshot,
): PlanRouteTabsState => {
  const active = findActiveTab(state)
  if (!isSnapshotEmpty(active.snapshot) && canAddTab(state)) {
    return addTab(state, snapshot)
  }
  return updateTabSnapshot(state, active.id, snapshot)
}

export const snapshotFromSavedRoute = (
  route: SavedRouteState,
): PlanRouteSnapshot => ({
  temporaryRoute: route.temporaryRoute,
  temporaryRouteInfo: route.temporaryRouteInfo,
  selectedAvoidOptions: route.selectedAvoidOptions ?? [],
  selectedLoadType: route.selectedLoadType ?? [],
  selectedVehicleType: route.selectedVehicleType ?? "tir",
  driverCarConfig: route.driverCarConfig,
  fuelConsumption: route.fuelConsumption,
  messageText: route.messageText,
  alternativeRoutes: route.alternativeRoutes,
  perLegAlternatives: route.perLegAlternatives,
})

const COORDINATES = /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/

const shortStopName = (stop: RouteStop): string | undefined => {
  const { locality, companyName, formattedAddress } = stop.location ?? {}
  if (locality) return locality
  if (companyName) return companyName
  if (formattedAddress && !COORDINATES.test(formattedAddress.trim())) {
    return formattedAddress.split(",")[0].trim() || undefined
  }
  return undefined
}

// "Jan Kowalski → Milano" for a route planned from a driver's position,
// "Wrocław → Milano" otherwise.
export const routeLabel = (
  snapshot?: PlanRouteSnapshot,
): string | undefined => {
  const stops = (snapshot?.temporaryRoute?.stops ?? []).filter(
    (stop) =>
      stop.action !== "passthrough" && stop.action !== "from_driver_location",
  )
  const driverName = snapshot?.selectedDriver?.driverName
  const origin = stops.length > 1 ? shortStopName(stops[0]) : undefined
  const destination = stops.length
    ? shortStopName(stops[stops.length - 1])
    : undefined

  if (driverName) {
    return destination ? `${driverName} → ${destination}` : driverName
  }
  if (origin && destination && origin !== destination) {
    return `${origin} → ${destination}`
  }
  return destination
}

// Components pass their own `t` so the label follows a language switch.
export const tabLabel = (
  tab: PlanRouteTab,
  t: TFunction = i18n.t.bind(i18n),
): string =>
  tab.name ??
  routeLabel(tab.snapshot) ??
  t("planOrder.tabs.defaultName", { number: tab.number })

export const tabsStorageKey = (userJid?: string): string =>
  userJid ? `${STORAGE_KEY}:${userJid}` : STORAGE_KEY

const isValidTab = (tab: unknown): tab is PlanRouteTab =>
  typeof tab === "object" &&
  tab !== null &&
  typeof (tab as PlanRouteTab).id === "string" &&
  typeof (tab as PlanRouteTab).number === "number"

export const loadTabsState = (storageKey: string): PlanRouteTabsState => {
  try {
    const stored = localStorage.getItem(storageKey)
    if (!stored) return initialTabsState()
    const parsed = JSON.parse(stored) as Partial<PlanRouteTabsState>
    const tabs = Array.isArray(parsed.tabs)
      ? parsed.tabs.filter(isValidTab).slice(0, MAX_PLAN_ROUTE_TABS)
      : []
    if (tabs.length === 0) return initialTabsState()
    const activeTabId = tabs.some((tab) => tab.id === parsed.activeTabId)
      ? (parsed.activeTabId as string)
      : tabs[0].id
    return { tabs, activeTabId }
  } catch (error) {
    console.error("Failed to load plan route tabs:", error)
    return initialTabsState()
  }
}

const withoutAlternatives = (
  tab: PlanRouteTab,
  keep: (tab: PlanRouteTab) => boolean,
): PlanRouteTab =>
  keep(tab) || !tab.snapshot
    ? tab
    : {
        ...tab,
        snapshot: {
          ...tab.snapshot,
          alternativeRoutes: undefined,
          perLegAlternatives: undefined,
        },
      }

// Alternatives are the bulk of a stored route, and the planner fetches them
// again for a restored route that has none. When the quota is hit they are
// dropped first from the tabs in the background, then from the active one.
// If even that does not fit the tabs stay in memory for this session only.
export const persistTabsState = (
  storageKey: string,
  state: PlanRouteTabsState,
): void => {
  const attempts: PlanRouteTabsState[] = [
    state,
    {
      ...state,
      tabs: state.tabs.map((tab) =>
        withoutAlternatives(tab, (t) => t.id === state.activeTabId),
      ),
    },
    {
      ...state,
      tabs: state.tabs.map((tab) => withoutAlternatives(tab, () => false)),
    },
  ]
  for (const attempt of attempts) {
    try {
      localStorage.setItem(storageKey, JSON.stringify(attempt))
      return
    } catch {
      // try a smaller version
    }
  }
  console.warn("Plan route tabs do not fit in localStorage; not saved")
}
